import React from 'react';
import { Building, Truck, Clock, CheckCircle, XCircle, Play, CreditCard, Eye, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { WithdrawalRequest } from '../../lib/api';
import { formatCurrency } from '../../lib/formatters';

interface WithdrawalKanbanBoardProps {
    withdrawals: WithdrawalRequest[];
    onApprove: (withdrawal: WithdrawalRequest) => void;
    onReject: (withdrawal: WithdrawalRequest) => void;
    onProcess: (withdrawal: WithdrawalRequest) => void;
    onComplete: (withdrawal: WithdrawalRequest) => void;
    onViewDetails: (withdrawal: WithdrawalRequest) => void;
    loading?: boolean;
}

interface KanbanColumn {
    id: string;
    title: string;
    status: string;
    color: string;
    bgColor: string;
    borderColor: string;
    icon: React.ReactNode;
}

const columns: KanbanColumn[] = [
    {
        id: 'pending',
        title: 'Pending Review',
        status: 'pending',
        color: 'text-amber-700',
        bgColor: 'bg-amber-50 dark:bg-amber-900/20',
        borderColor: 'border-amber-200 dark:border-amber-800',
        icon: <Clock className="h-4 w-4" />,
    },
    {
        id: 'approved',
        title: 'Approved',
        status: 'approved',
        color: 'text-blue-700',
        bgColor: 'bg-blue-50 dark:bg-blue-900/20',
        borderColor: 'border-blue-200 dark:border-blue-800',
        icon: <CheckCircle className="h-4 w-4" />,
    },
    {
        id: 'processing',
        title: 'Processing',
        status: 'processing',
        color: 'text-purple-700',
        bgColor: 'bg-purple-50 dark:bg-purple-900/20',
        borderColor: 'border-purple-200 dark:border-purple-800',
        icon: <Play className="h-4 w-4" />,
    },
    {
        id: 'completed',
        title: 'Completed',
        status: 'completed',
        color: 'text-emerald-700',
        bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
        borderColor: 'border-emerald-200 dark:border-emerald-800',
        icon: <CreditCard className="h-4 w-4" />,
    },
];

function formatTimeAgo(date: string): string {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now.getTime() - then.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

    if (diffDays > 0) {
        return `${diffDays}d ago`;
    } else if (diffHours > 0) {
        return `${diffHours}h ago`;
    } else {
        return 'Just now';
    }
}

interface WithdrawalCardProps {
    withdrawal: WithdrawalRequest;
    column: KanbanColumn;
    onApprove: () => void;
    onReject: () => void;
    onProcess: () => void;
    onComplete: () => void;
    onViewDetails: () => void;
}

function WithdrawalCard({
    withdrawal,
    column,
    onApprove,
    onReject,
    onProcess,
    onComplete,
    onViewDetails,
}: WithdrawalCardProps) {
    const isSupplier = withdrawal.entityType === 'supplier';

    const getActionButtons = () => {
        switch (withdrawal.status) {
            case 'pending':
                return (
                    <div className="flex gap-1">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onApprove(); }}
                            className="h-7 px-2 text-xs bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700"
                        >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approve
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); onReject(); }}
                            className="h-7 px-2 text-xs bg-red-50 hover:bg-red-100 border-red-200 text-red-700"
                        >
                            <XCircle className="h-3 w-3 mr-1" />
                            Reject
                        </Button>
                    </div>
                );
            case 'approved':
                return (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onProcess(); }}
                        className="h-7 px-2 text-xs bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700"
                    >
                        <Play className="h-3 w-3 mr-1" />
                        Start Processing
                    </Button>
                );
            case 'processing':
                return (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); onComplete(); }}
                        className="h-7 px-2 text-xs bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700"
                    >
                        <CreditCard className="h-3 w-3 mr-1" />
                        Mark Complete
                    </Button>
                );
            default:
                return null;
        }
    };

    return (
        <div
            onClick={onViewDetails}
            className={`
                relative p-3 rounded-lg border bg-white dark:bg-gray-900
                hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer
                group
            `}
        >
            {/* Entity type indicator */}
            <div className="absolute -left-0.5 top-3 bottom-3 w-1 rounded-full"
                style={{ backgroundColor: isSupplier ? '#3b82f6' : '#f97316' }}
            />

            {/* Header */}
            <div className="flex items-start justify-between mb-2 pl-2">
                <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded-lg ${isSupplier ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-orange-100 dark:bg-orange-900/30'}`}>
                        {isSupplier ? (
                            <Building className="h-3.5 w-3.5 text-blue-600" />
                        ) : (
                            <Truck className="h-3.5 w-3.5 text-orange-600" />
                        )}
                    </div>
                    <div className="min-w-0">
                        <p className="font-medium text-sm truncate">{withdrawal.entityName}</p>
                        <p className="text-xs text-muted-foreground">{withdrawal.requestId}</p>
                    </div>
                </div>
                <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => { e.stopPropagation(); onViewDetails(); }}
                >
                    <Eye className="h-3.5 w-3.5" />
                </Button>
            </div>

            {/* Amount */}
            <div className="pl-2 mb-3">
                <p className="text-xl font-bold">{formatCurrency(withdrawal.amount)}</p>
            </div>

            {/* Bank info */}
            <div className="pl-2 mb-3 p-2 rounded-md bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs text-muted-foreground truncate">
                    {withdrawal.bankDetails?.bankName || 'Bank'} • {withdrawal.bankDetails?.accountNumberMasked}
                </p>
            </div>

            {/* Footer: Time + Actions */}
            <div className="pl-2 flex items-center justify-between">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    {formatTimeAgo(withdrawal.requestedAt)}
                </span>
                {getActionButtons()}
            </div>
        </div>
    );
}

function KanbanColumnComponent({
    column,
    withdrawals,
    onApprove,
    onReject,
    onProcess,
    onComplete,
    onViewDetails,
}: {
    column: KanbanColumn;
    withdrawals: WithdrawalRequest[];
    onApprove: (w: WithdrawalRequest) => void;
    onReject: (w: WithdrawalRequest) => void;
    onProcess: (w: WithdrawalRequest) => void;
    onComplete: (w: WithdrawalRequest) => void;
    onViewDetails: (w: WithdrawalRequest) => void;
}) {
    const columnWithdrawals = withdrawals.filter(w => w.status === column.status);
    const totalAmount = columnWithdrawals.reduce((sum, w) => sum + w.amount, 0);

    return (
        <div className="flex-1 min-w-[280px] max-w-[340px]">
            {/* Column Header */}
            <div className={`p-3 rounded-t-xl ${column.bgColor} border ${column.borderColor}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <span className={column.color}>{column.icon}</span>
                        <span className={`font-semibold text-sm ${column.color}`}>{column.title}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${column.bgColor} ${column.color}`}>
                        {columnWithdrawals.length}
                    </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                    {formatCurrency(totalAmount)} total
                </p>
            </div>

            {/* Column Body */}
            <div className={`
                min-h-[400px] max-h-[600px] overflow-y-auto
                p-2 space-y-2 rounded-b-xl
                bg-gray-50/50 dark:bg-gray-900/50
                border-x border-b ${column.borderColor}
            `}>
                {columnWithdrawals.length > 0 ? (
                    columnWithdrawals.map((withdrawal) => (
                        <WithdrawalCard
                            key={withdrawal._id}
                            withdrawal={withdrawal}
                            column={column}
                            onApprove={() => onApprove(withdrawal)}
                            onReject={() => onReject(withdrawal)}
                            onProcess={() => onProcess(withdrawal)}
                            onComplete={() => onComplete(withdrawal)}
                            onViewDetails={() => onViewDetails(withdrawal)}
                        />
                    ))
                ) : (
                    <div className="flex flex-col items-center justify-center h-32 text-center p-4">
                        <div className={`p-3 rounded-full ${column.bgColor} mb-2`}>
                            {column.icon}
                        </div>
                        <p className="text-sm text-muted-foreground">No {column.title.toLowerCase()} items</p>
                    </div>
                )}
            </div>
        </div>
    );
}

export function WithdrawalKanbanBoard({
    withdrawals,
    onApprove,
    onReject,
    onProcess,
    onComplete,
    onViewDetails,
    loading = false,
}: WithdrawalKanbanBoardProps) {
    if (loading) {
        return (
            <div className="flex gap-4 overflow-x-auto pb-4">
                {columns.map((column) => (
                    <div key={column.id} className="flex-1 min-w-[280px] max-w-[340px]">
                        <div className={`p-3 rounded-t-xl ${column.bgColor} border ${column.borderColor} animate-pulse`}>
                            <div className="h-4 bg-gray-200 rounded w-24" />
                        </div>
                        <div className="min-h-[400px] p-2 space-y-2 rounded-b-xl bg-gray-50/50 border-x border-b">
                            {[1, 2].map((i) => (
                                <div key={i} className="h-32 bg-gray-100 rounded-lg animate-pulse" />
                            ))}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="flex gap-4 overflow-x-auto pb-4 -mx-2 px-2">
            {columns.map((column) => (
                <KanbanColumnComponent
                    key={column.id}
                    column={column}
                    withdrawals={withdrawals}
                    onApprove={onApprove}
                    onReject={onReject}
                    onProcess={onProcess}
                    onComplete={onComplete}
                    onViewDetails={onViewDetails}
                />
            ))}
        </div>
    );
}

export default WithdrawalKanbanBoard;
